import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from "react-router-dom";
import useScrollReveal from '../hooks/useScrollReveal';
import { createFadeIn } from '../utils/animations';
import './AboutPage.css';

// ─── Neural Network Visual ─────────────────────────────────────────────────────

const NeuralVisual = () => {
  const cx = 250;
  const cy = 188;
  const r = 108;

  const satellites = [
    { label: 'Understand', angle: -90 },
    { label: 'Learn', angle: -18 },
    { label: 'Adapt', angle: 54 },
    { label: 'Communicate', angle: 126 },
    { label: 'Remember', angle: 198 },
  ].map(n => ({
    ...n,
    x: cx + r * Math.cos((n.angle * Math.PI) / 180),
    y: cy + r * Math.sin((n.angle * Math.PI) / 180),
  }));

  const crossLinks = [[0, 1], [1, 2], [2, 3], [3, 4], [4, 0], [0, 3]];

  return (
    <div className="about-neural-visual">
      <svg
        className="about-neural-svg"
        viewBox="0 0 500 375"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Outer ring */}
        <circle cx={cx} cy={cy} r="148" fill="none" stroke="rgba(26,43,74,0.05)" strokeWidth="1" strokeDasharray="4 6" />
        <circle cx={cx} cy={cy} r="72" fill="none" stroke="rgba(26,43,74,0.06)" strokeWidth="1" />

        {/* Cross links between satellites */}
        {crossLinks.map(([a, b], i) => (
          <line
            key={`cross-${i}`}
            x1={satellites[a].x} y1={satellites[a].y}
            x2={satellites[b].x} y2={satellites[b].y}
            stroke="rgba(26,43,74,0.06)"
            strokeWidth="1"
          />
        ))}

        {/* Spoke lines from center to satellites */}
        {satellites.map((n, i) => (
          <line
            key={`spoke-${i}`}
            x1={cx} y1={cy}
            x2={n.x} y2={n.y}
            stroke="rgba(26,43,74,0.14)"
            strokeWidth="1.5"
            strokeDasharray="4 5"
          />
        ))}

        {/* Central node */}
        <circle cx={cx} cy={cy} r="46" fill="var(--accent)" />
        <circle cx={cx} cy={cy} r="54" fill="none" stroke="rgba(26,43,74,0.18)" strokeWidth="1.5" />
        <text x={cx} y={cy - 5} textAnchor="middle" fill="#FAFAF8" fontSize="14" fontWeight="600" fontFamily="Inter, sans-serif">
          Rusiv
        </text>
        <text x={cx} y={cy + 12} textAnchor="middle" fill="rgba(250,250,248,0.6)" fontSize="7.5" fontFamily="Inter, sans-serif" letterSpacing="1.5">
          PERSONAL AI
        </text>

        {/* Satellite nodes */}
        {satellites.map((n, i) => (
          <g key={`node-${i}`}>
            <circle cx={n.x} cy={n.y} r="28" fill="var(--bg-primary)" stroke="rgba(26,43,74,0.18)" strokeWidth="1.5" />
            <circle cx={n.x} cy={n.y} r="28" fill="rgba(26,43,74,0.02)" />
            <text x={n.x} y={n.y + 4} textAnchor="middle" fill="var(--accent)" fontSize="8" fontWeight="500" fontFamily="Inter, sans-serif">
              {n.label}
            </text>
          </g>
        ))}

        {/* Ambient dots */}
        {[
          [68, 42, 3], [434, 318, 2.5], [458, 64, 2],
          [44, 316, 2], [260, 352, 2.5], [18, 190, 2],
          [482, 188, 2], [130, 348, 2],
        ].map(([x, y, rad], i) => (
          <circle key={`dot-${i}`} cx={x} cy={y} r={rad} fill="rgba(26,43,74,0.15)" />
        ))}
      </svg>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

const AboutPage = () => {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);

  useScrollReveal();

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 60);
    window.scrollTo(0, 0);
    return () => clearTimeout(timer);
  }, []);

  const fi = createFadeIn(mounted);

  // ── Data ──────────────────────────────────────────

  const problems = [
    {
      icon: '∅',
      title: 'Generic Answers',
      desc: 'The same response for a beginner and an expert, for someone in a hurry and someone who wants depth.',
    },
    {
      icon: '≈',
      title: 'No Memory of You',
      desc: 'Every conversation starts over. You explain who you are again and again.',
    },
    {
      icon: '◌',
      title: 'One Communication Style',
      desc: 'The same tone and structure, regardless of what the moment actually calls for.',
    },
    {
      icon: '✕',
      title: 'You Are a Prompt',
      desc: 'To most systems, you are anonymous text, not a person who keeps changing.',
    },
  ];

  const whyCards = [
    {
      title: 'The Company Behind Rusiv',
      desc: 'Allyverto researches and builds the personal AI systems Rusiv runs on. Rusiv is the first result, not the whole plan.',
    },
    {
      title: 'Starting Where It Matters',
      desc: 'Every learner understands differently, which makes education the clearest place to prove personalization actually works.',
    },
    {
      title: 'Earning Trust Deliberately',
      desc: "We would rather move carefully and explain ourselves than ask people to trust a system they can't see into.",
    },
  ];

  const principles = [
    {
      num: '01',
      title: 'YOU over Assumptions',
      desc: "We would rather ask a thoughtful question than assume who you are.",
    },
    {
      num: '02',
      title: 'Transparent by Design',
      desc: 'You can see and shape how your context is used. Personalization should never feel hidden.',
    },
    {
      num: '03',
      title: 'Growth over Guesswork',
      desc: 'Rusiv learns useful, changing patterns about you, not permanent labels.',
    },
    {
      num: '04',
      title: 'Human Centered Intelligence',
      desc: 'Technology should amplify human judgment, not substitute for it.',
    },
  ];

  // ── Render ────────────────────────────────────────

  return (
    <div style={{ background: 'var(--bg-primary)', paddingTop: '80px', overflowX: 'hidden' }}>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ HERO ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="about-hero">
        <div className="container">
          <div className="about-hero-inner">

            {/* Left: Copy */}
            <div>
              <h1
                style={{
                  ...fi(0.2),
                  fontFamily: 'var(--font-serif)',
                  fontStyle: 'italic',
                  fontSize: 'clamp(2.8rem, 6vw, 5rem)',
                  lineHeight: '1.08',
                  marginBottom: '1.5rem',
                }}
              >
                Building AI that{' '}
                <span style={{ fontStyle: 'normal', color: 'var(--accent)' }}>begins with you.</span>
              </h1>

              <p
                style={{
                  ...fi(0.35),
                  fontSize: 'clamp(1rem, 1.8vw, 1.2rem)',
                  lineHeight: '1.8',
                  color: 'var(--text-secondary)',
                  marginBottom: '2.5rem',
                  maxWidth: '460px',
                }}
              >
                Allyverto is the company building Rusiv. We started here because
                most AI still treats every person who talks to it the same way.
              </p>

              <a className="about-product-badge" style={fi(0.5)} href='https://rusiv.com/' target="_blank" rel="noopener noreferrer">
                <span className="about-product-label">Our flagship product</span>
                <span className="about-product-name">Rusiv</span>
                <span className="about-product-desc">
                  Allyverto's personal AI.
                </span>
              </a>
            </div>

            {/* Right: Visual */}
            <div style={fi(0.4)}>
              <NeuralVisual />
            </div>

          </div>
        </div>
      </section>


      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ THE PROBLEM ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section style={{ padding: 'clamp(5rem, 10vw, 8rem) 0' }}>
        <div className="container">
          <div className="about-problem-grid">

            {/* Left: Context */}
            <div data-scroll>
              <div className="about-section-label">The Challenge</div>
              <h2
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'clamp(2.2rem, 4.5vw, 3.5rem)',
                  lineHeight: '1.1',
                  marginBottom: '1.5rem',
                }}
              >
                AI has a{' '}
                <span style={{ fontStyle: 'italic', color: 'var(--accent)' }}>YOU problem.</span>
              </h2>
              <p
                style={{
                  fontSize: '1.08rem',
                  lineHeight: '1.8',
                  color: 'var(--text-secondary)',
                  marginBottom: '1.25rem',
                }}
              >
                Today's AI can answer almost anything. It still knows almost
                nothing about the person asking. Two people can ask the exact
                same question and need two completely different answers.
              </p>
              <p style={{ fontSize: '0.98rem', lineHeight: '1.75', color: 'var(--text-muted)' }}>
                This isn't a missing feature. It's a missing foundation.
                We built Allyverto to fix it.
              </p>
              <Link to="/rusiv#difference" className="about-challenge-link">
                See exactly how Rusiv responds differently
                <span aria-hidden="true">→</span>
              </Link>
            </div>

            {/* Right: Problem list */}
            <div className="about-problem-list">
              {problems.map((p, i) => (
                <div
                  key={i}
                  className="about-problem-row"
                  data-scroll
                  style={{ transitionDelay: `${i * 0.1}s` }}
                >
                  <span className="about-problem-symbol">{p.icon}</span>
                  <div>
                    <h4>{p.title}</h4>
                    <p>{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ WHY ALLYVERTO ━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section style={{ padding: 'clamp(5rem, 10vw, 8rem) 0' }}>
        <div className="container">

          <div data-scroll style={{ textAlign: 'center', marginBottom: 'clamp(3rem, 6vw, 5rem)' }}>
            <div className="about-section-label">Our Edge</div>
            <h2
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 'clamp(2.2rem, 4.5vw, 3.5rem)',
              }}
            >
              Why{' '}
              <span style={{ fontStyle: 'italic', color: 'var(--accent)' }}>Allyverto</span>
            </h2>
          </div>

          <div className="about-why-list">
            {whyCards.map((w, i) => (
              <div
                key={i}
                className="about-why-row"
                data-scroll
                style={{ transitionDelay: `${i * 0.12}s` }}
              >
                <span className="about-why-number">0{i + 1}</span>
                <div className="about-why-copy">
                  <h3>{w.title}</h3>
                  <p>{w.desc}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ PRINCIPLES ━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section style={{ padding: 'clamp(5rem, 10vw, 8rem) 0' }}>
        <div className="container">
          <div className="about-principles-grid">

            <div data-scroll>
              <div className="about-section-label">Our Principles</div>
              <h2
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'clamp(2.2rem, 4.5vw, 3.5rem)',
                  lineHeight: '1.15',
                }}
              >
                What we{' '}
                <span style={{ fontStyle: 'italic', color: 'var(--accent)' }}>stand for</span>
              </h2>
            </div>

            <div className="about-principles-list">
              {principles.map((p, i) => (
                <div
                  key={i}
                  className="about-principle-item"
                  data-scroll
                  style={{ transitionDelay: `${i * 0.1}s` }}
                >
                  <span className="about-principle-num">{p.num}</span>
                  <div>
                    <h4
                      style={{
                        fontSize: '1rem',
                        fontWeight: 600,
                        marginBottom: '0.3rem',
                        color: 'var(--text-primary)',
                      }}
                    >
                      {p.title}
                    </h4>
                    <p
                      style={{
                        fontSize: '0.88rem',
                        lineHeight: '1.65',
                        color: 'var(--text-muted)',
                        margin: 0,
                      }}
                    >
                      {p.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <p className="about-principles-footnote">
              For the specific controls this gives you over Rusiv's memory, see{' '}
              <Link to="/rusiv#privacy">Privacy and Control on the product page</Link>.
            </p>

          </div>
        </div>
      </section>

      

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ CTA ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="about-cta" style={{ padding: 'clamp(6rem, 12vw, 10rem) 0' }}>
        <div
          className="container-narrow"
          style={{ textAlign: 'center', position: 'relative' }}
        >
          <div data-scroll>
            <div className="about-section-label">Join the Journey</div>
            <h2
              style={{
                fontFamily: 'var(--font-serif)',
                fontStyle: 'italic',
                fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
                lineHeight: '1.1',
                marginBottom: '1.25rem',
                color: 'var(--text-primary)',
              }}
            >
              We're just getting started.
            </h2>
            <p
              style={{
                fontSize: '1.08rem',
                lineHeight: '1.75',
                color: 'var(--text-secondary)',
                maxWidth: '460px',
                margin: '0 auto 2.5rem',
              }}
            >
              Rusiv is in active development. Follow along as we build it
              in the open.
            </p>
            <div className="about-cta-buttons">
              <button
                className="btn alluse-button alluse-exe-link-button"
                onClick={() =>
                  window.open("https://rusiv.com/", "_blank", "noopener,noreferrer")
                }
              >
                Try Rusiv
              </button>
              <button className="btn alluse-button" 
                onClick={() => navigate("/vision")}>
                  Explore Our Vision
              </button>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default AboutPage;
